/**
 * Banking API helpers.
 *
 * GET requests: use the SWR keys defined here directly with useSWR — the global
 * fetcher in _app.tsx / app/layout.tsx resolves them against NEXT_PUBLIC_API_HOST.
 *
 * Mutations (POST / DELETE): use the `useApi` hook from @hooks/useApi which
 * dispatches through the Redux API middleware (axios-based, handles auth/errors).
 */

const base = (clubSlug: string) => `club/${clubSlug}/banking`;

/** SWR cache keys / GET URLs — pass directly to useSWR */
export const bankingKeys = {
    connection: (clubSlug: string) => `${base(clubSlug)}/connection`,
    accounts: (clubSlug: string) => `${base(clubSlug)}/accounts`,
    /** Club-wide list for the transactions table — the account is a filter value, not part of
     *  the URL, so switching accounts keeps the table (and its persisted filters) in place. */
    clubTransactions: (clubSlug: string) => `${base(clubSlug)}/transactions`,
    transactions: (clubSlug: string, accountId: number, params: Record<string, string | number | undefined>) => {
        const qs = new URLSearchParams(
            Object.fromEntries(
                Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
            ) as Record<string, string>
        ).toString();
        return `${base(clubSlug)}/accounts/${accountId}/transactions${qs ? `?${qs}` : ''}`;
    },
    analyticsOverview: (clubSlug: string, accountId?: number) =>
        `${base(clubSlug)}/analytics/overview${accountId ? `?account_id=${accountId}` : ''}`,
    analyticsCashflow: (clubSlug: string) => `${base(clubSlug)}/analytics/cashflow`,
    analyticsCategories: (clubSlug: string) => `${base(clubSlug)}/analytics/categories`,
    analyticsRecurring: (clubSlug: string) => `${base(clubSlug)}/analytics/recurring`,
};

/** Mutation URLs — use with `useApi()` from @hooks/useApi */
export const bankingMutationUrls = {
    startConnect: (clubSlug: string) => `${base(clubSlug)}/connection`,
    refreshConsent: (clubSlug: string) => `${base(clubSlug)}/connection/refresh`,
    disconnect: (clubSlug: string) => `${base(clubSlug)}/connection`,
    syncAccounts: (clubSlug: string) => `${base(clubSlug)}/accounts/sync`,
    /** Fetches current data from the bank — `syncAccounts` only re-reads the provider's copy. */
    refreshBankData: (clubSlug: string) => `${base(clubSlug)}/accounts/refresh`,
    /** Current assignment state of single rows — lets the table refresh them in place. */
    transactionAssignments: (clubSlug: string) => `${base(clubSlug)}/transactions/assignments`,
};
